import {
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core';

import { genericGetAttributeColumns } from '@features/budget-attributes/attribute-columns';

import {
  AgColumnGroupHeaderComponent,
  AgColumnGroupHeaderExternalParams,
} from '@shared/ag-components/ag-column-group-header/ag-column-group-header';
import { AgNotesComponent } from '@shared/ag-components/ag-notes/ag-notes.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { AgSetColumnsVisible } from '@shared/utils';
import { getCellClassForGroups } from '@shared/utils/ag-cell-class-for-groups';
import { agWidthOfAColumn } from '@shared/utils/ag-width-of-a-column';
import { AuxExcelStyleKeys } from '@shared/utils/aux-excel-styles/aux-excel-style-keys';

import { qcAttributesColumnGroup } from './qc-attributes-column-group';
import { qcGetCostCategoryColumn } from './qc-cost-category-column';
import { qcGetMoneyColumn, QuarterCloseGridConfigContext } from './qc-grid-config-context';
import { QCGridData } from '../../../../period-close.model';
import { QuarterCloseService } from '../../quarter-close.service';

// eslint-disable-next-line max-lines-per-function
export const qcVendorContractColumnGroup = (
  ctx: QuarterCloseGridConfigContext,
  quarterCloseService: QuarterCloseService,
  isFluxAnalysisAvailableAndOn: boolean,
  openReviewNotesModal: (params: CellClickedEvent) => Promise<void>
): ColGroupDef => {
  const isPivotSelected = !!ctx.attributeConfig.primaryAttribute.attribute_name;
  const attributeColumns = genericGetAttributeColumns({
    attributeConfig: ctx.attributeConfig,
    colDefOverrides: {
      cellClass: getCellClassForGroups,
      columnGroupShow: 'open',
      headerClass: ['text-center'],
      minWidth: 70,
      pinned: 'left',
      resizable: true,
    },
    onlyPivot: true,
  });

  const openByDefault =
    localStorage.getItem(LocalStorageKey.CLOSE_SUMMARY_VENDOR_CONTRACT_GROUP) === 'true';

  const attributesColumnGroup = qcAttributesColumnGroup(ctx, isFluxAnalysisAvailableAndOn);

  quarterCloseService.attributesSectionColumns = attributesColumnGroup.children.map(
    (child) => (child as ColDef).field!
  );

  const columnsForToggle = () => {
    if (localStorage.getItem(LocalStorageKey.FLUX_ANALYSIS_MODE) === 'true') {
      return [];
    }

    return attributesColumnGroup.children.map((child) => (child as ColDef).field!);
  };

  return {
    children: [
      {
        cellClass: getCellClassForGroups,
        field: 'vendor_id',
        filterValueGetter: (params: ValueGetterParams<QCGridData>) => {
          return params.data?.vendor_name || '';
        },
        headerClass: ['text-center'],
        headerName: 'Name',
        pinned: 'left',
        valueFormatter: (params: ValueFormatterParams<QCGridData>) => {
          if (params.node?.isRowPinned()) {
            return 'Total';
          }

          return params.data?.vendor_name || '';
        },
        width: 230,
      },
      {
        ...qcGetMoneyColumn(),
        columnGroupShow: 'open',
        field: 'qmonth1_total_expense_quote',
        filter: 'agNumberColumnFilter',
        headerName: 'Contract Amount',
        hide: !ctx.flags.isContractAmountColumnVisible || !ctx.isCurrentQuarterSelected,
        pinned: 'left',
        width: agWidthOfAColumn({
          field: 'qmonth1_total_expense_quote',
          gridData: ctx.gridData,
          headerName: 'Contract Amount',
        }),
      },
      ...(isPivotSelected ? attributeColumns : [qcGetCostCategoryColumn(ctx)]),
      {
        cellClass: () => {
          const classes = ['wrapText'];

          if (ctx.isCloseMonthsProcessing) {
            classes.push('opacity-50');
            classes.push('cursor-not-allowed');
          } else {
            classes.push('cursor-pointer');
          }

          return classes;
        },
        cellRendererParams: () => {
          const closeMonthsProcessing = ctx.isCloseMonthsProcessing
            ? 'Notes modal can be used once month close finishes processing.'
            : '';

          return {
            tooltip: closeMonthsProcessing,
          };
        },
        cellRendererSelector: (params) => {
          if (params.node.rowPinned) {
            return {};
          }

          return { component: AgNotesComponent, params };
        },
        columnGroupShow: 'open',
        field: 'number_of_notes_for_selected_quarter',
        filter: 'agNumberColumnFilter',
        headerName: 'Review Notes',
        minWidth: 135,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onCellClicked: async (event: CellClickedEvent) => {
          if (!ctx.isCloseMonthsProcessing) {
            await openReviewNotesModal(event);
          }
        },
        pinned: 'left',
        sortable: true,
        width: 135,
      },
    ],
    headerClass: ['text-center', AuxExcelStyleKeys.ALTERNATE],
    headerGroupComponent: AgColumnGroupHeaderComponent,
    headerGroupComponentParams: <AgColumnGroupHeaderExternalParams>{
      onToggle: (isOpen: boolean, params) => {
        AgSetColumnsVisible({
          gridApi: params.api,
          keys: [...columnsForToggle()],
          visible: isOpen,
        });
        localStorage.setItem(LocalStorageKey.CLOSE_SUMMARY_VENDOR_CONTRACT_GROUP, String(isOpen));
      },
    },
    headerName: 'Vendor Contract',
    openByDefault,
  };
};
